/**
 * The point dialog: the single editing surface for a point in time.
 *
 * This is `YearPrompt` grown up. The chart tap that used to ask only for a year
 * now opens this dialog to create *or* edit a point, "Add point" opens it in
 * create mode, and it is the only place an entry can be deleted. Everything the
 * old prompt got right is kept:
 *
 * - A real `<dialog>` opened with `showModal()`, so the top layer, the focus
 *   trap, the inert background and Escape come from the platform.
 * - The component does not know whether it is open: `target` is that signal and
 *   the caller owns it, so `target === null` renders nothing and the dialog can
 *   never outlive the action that opened it. Escape, the Cancel button, the
 *   platform's `cancel` event and a click on the backdrop are all the same path
 *   (`onCancel`); the component never closes itself, and the path is guarded so
 *   one dismissal cannot call `onCancel` twice.
 * - A backdrop click is told apart from a click on the dialog's padding and from
 *   a text selection released over the backdrop by its coordinates and by where
 *   the press that started it began — see `handleDialogClick`.
 * - `showModal()` is guarded the way the chart guards `setPointerCapture`:
 *   jsdom does not implement it, and without it the dialog still renders, it is
 *   just not modal.
 * - Focus: `showModal` focuses the dialog, not a field, so the year field is
 *   focused explicitly and its text selected, because typing over the year is
 *   the common edit. Closing restores focus to whatever had it when the dialog
 *   opened — the browser would do it on `close()`, but React removes the element
 *   instead of the platform closing it, so it is restored here. If that element
 *   was deleted (this dialog is also the delete path), the focus call is a
 *   no-op and the caller moves focus on, as the App flow describes. That
 *   restore passes `preventScroll: true`: a legend row can be far down the
 *   page, and a plain `focus()` scrolls to whatever it focuses, which drags the
 *   viewport away from the chart the user was working on.
 *
 * Validation is deferred, exactly as the old prompt's was: nothing is flagged
 * while a field is being typed in, and a save attempt with anything invalid
 * marks *every* offending field and saves nothing. A marked field clears the
 * moment its text parses again, so a flag cannot stick to a fixed value. This is
 * also why a coordinate `-` alone is allowed in the field but refused on save.
 *
 * Two fields, one dialog: the year and both coordinates are validated one way
 * and committed together, so a partial edit can never reach the caller. The
 * caller gets numbers, normalised: coordinates through `roundCoord` so the
 * dialog cannot introduce more precision than the store, the share link and the
 * display agree on, and a description trimmed of the spaces a paste tends to
 * leave behind.
 *
 * Layout: the four fields used to stack, which made the dialog taller than it
 * needs to be on a phone. Year owns a row, the two coordinates share one grid
 * row (`.point-dialog__coords`) with a real label, description and error line
 * each, and the description is full width again — the labelled-field semantics
 * are untouched by the shorter box.
 *
 * Styling: this file owns no CSS. It reuses the app's classes (`card`,
 * `card__title`, `field__input`, `field__error`, `field__count`, `button`
 * variants) and the `point-dialog*` box/form/coords/actions classes in app.css.
 */

import { useEffect, useId, useRef, useState } from 'react'
import type {
  FormEvent,
  KeyboardEvent,
  MouseEvent,
  PointerEvent,
  ReactElement,
  Ref,
  SyntheticEvent,
} from 'react'

import { formatValue, isValidCoord, isValidYear, roundCoord } from '../lib/compass'
import { MAX_DESCRIPTION_LENGTH } from '../lib/storage'
import type { Entry } from '../lib/types'

/** What the dialog is open for, or `null` when it is closed. */
export type PointDialogTarget =
  | { mode: 'create'; x: number; y: number; year: number }
  | { mode: 'edit'; entry: Entry }

/** The normalised values a confirmed dialog hands to its caller. */
export interface PointDialogValues {
  year: number
  x: number
  y: number
  description: string
}

export interface PointDialogProps {
  /** `null` closes the dialog. */
  target: PointDialogTarget | null
  onSave: (values: PointDialogValues) => void
  /** Rendered only in `edit` mode; the caller deletes and closes. */
  onDelete: () => void
  onCancel: () => void
}

/** Field messages. Short, because they are read without their label. */
const YEAR_ERROR = 'Use 1000 to 2999.'
const COORD_ERROR = 'Use -10 to 10.'

/** Per-field messages; `null` means the field is not flagged. */
interface FieldErrors {
  year: string | null
  x: string | null
  y: string | null
}

const NO_ERRORS: FieldErrors = { year: null, x: null, y: null }

/**
 * Whether a client position falls outside a bounding rect.
 *
 * `::backdrop` has no node of its own: a click on it targets the dialog element
 * itself, exactly as a click on the dialog's own padding does, and so does a text
 * selection that begins in a field and is released over the backdrop. Only the
 * coordinates and the press that started it can tell those apart.
 */
function isOutside(rect: DOMRect, clientX: number, clientY: number): boolean {
  return clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom
}

/**
 * Whether the box can be measured at all.
 *
 * jsdom reports a zero rect for every element, and a real dialog that has not
 * been laid out reports the same. With no box there is no inside and no outside,
 * so the same reasoning as the chart's unmappable gesture applies: do nothing.
 */
function hasBox(rect: DOMRect): boolean {
  return rect.width > 0 && rect.height > 0
}

/** Parse input text into a number; blank is NaN, not 0 (`Number('') === 0`). */
function parseNumber(text: string): number {
  const trimmed = text.trim()
  return trimmed === '' ? Number.NaN : Number(trimmed)
}

/**
 * A stable identity for the target, so the open/reset effect does not restart
 * on every render.
 *
 * App passes a freshly built `target` object each render; keying the effect on
 * that object would reset the drafts on every keystroke. The key changes
 * exactly when a different point is being opened — and, for a create at new
 * coordinates, when those coordinates change.
 */
function targetKey(target: PointDialogTarget | null): string | null {
  if (target === null) return null
  return target.mode === 'edit'
    ? `edit:${target.entry.id}`
    : `create:${target.x}:${target.y}:${target.year}`
}

/** The text a fresh opening starts from: the edit target, or blanks when closed. */
interface Drafts {
  year: string
  x: string
  y: string
  description: string
}

function seedDrafts(target: PointDialogTarget | null): Drafts {
  if (target === null) return { year: '', x: '', y: '', description: '' }
  if (target.mode === 'edit') {
    return {
      year: String(target.entry.year),
      x: formatValue(target.entry.x),
      y: formatValue(target.entry.y),
      description: target.entry.description ?? '',
    }
  }
  return {
    year: String(target.year),
    x: formatValue(target.x),
    y: formatValue(target.y),
    description: '',
  }
}

interface NumericFieldProps {
  id: string
  /** Contract test id for this input. */
  testId: string
  label: string
  inputMode: 'decimal' | 'numeric'
  value: string
  error: string | null
  inputRef?: Ref<HTMLInputElement>
  onChange: (text: string) => void
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void
}

/**
 * One validated numeric input.
 *
 * `type="text"` rather than `type="number"` on purpose: a controlled number
 * input re-sanitises mid-edit values, so typing `-`, `-3.` or an empty field is
 * rewritten out from under the typist. `inputMode` still asks a phone for the
 * right keyboard, and validation is done by the parser.
 *
 * Local and used three times (year, x, y): all three need identical
 * `aria-invalid` / `aria-describedby` / Enter-to-submit wiring, and repeating
 * it by hand is how one field quietly drifts.
 */
function NumericField({
  id,
  testId,
  label,
  inputMode,
  value,
  error,
  inputRef,
  onChange,
  onKeyDown,
}: NumericFieldProps): ReactElement {
  const errorId = `${id}-error`

  return (
    <div className="point-dialog__field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        ref={inputRef}
        className="field__input"
        data-testid={testId}
        type="text"
        inputMode={inputMode}
        autoComplete="off"
        value={value}
        aria-invalid={error !== null ? true : undefined}
        aria-describedby={errorId}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
      />
      {/* Always mounted, one line high (`.field__error`): flagging a field must
          not move the buttons, and a live region has to exist before its text
          changes for a screen reader to announce the message. */}
      <p className="field__error" id={errorId} role="alert">
        {error ?? ''}
      </p>
    </div>
  )
}

export default function PointDialog({
  target,
  onSave,
  onDelete,
  onCancel,
}: PointDialogProps): ReactElement | null {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const yearRef = useRef<HTMLInputElement>(null)
  const xRef = useRef<HTMLInputElement>(null)
  const yRef = useRef<HTMLInputElement>(null)
  /** Whatever had focus before the dialog opened; focused again when it closes. */
  const returnFocusRef = useRef<HTMLElement | null>(null)
  /**
   * Escape can arrive twice in a browser — our key handler and the platform's
   * `cancel` — and the caller expects one cancel per opening.
   */
  const cancelHandledRef = useRef(false)
  /**
   * Whether the press that preceded the current click started outside the
   * dialog's box. See `handleDialogClick` for why the pair is needed.
   */
  const pointerDownOutsideRef = useRef(false)

  const [yearDraft, setYearDraft] = useState('')
  const [xDraft, setXDraft] = useState('')
  const [yDraft, setYDraft] = useState('')
  const [descriptionDraft, setDescriptionDraft] = useState('')
  const [errors, setErrors] = useState<FieldErrors>(NO_ERRORS)

  const key = targetKey(target)
  const [seededKey, setSeededKey] = useState<string | null>(null)

  // Reset the drafts during render, not in an effect: the effect focuses and
  // selects the year field, and a selection made before React commits the
  // prefilled value would be collapsed when it does. This is React's own
  // "reset state when a prop changes" pattern — keyed on `key`, so App's fresh
  // target object per render does not wipe a draft mid-typing, and a save
  // attempt's error flags survive every other re-render.
  if (seededKey !== key) {
    const seed = seedDrafts(target)
    setSeededKey(key)
    setYearDraft(seed.year)
    setXDraft(seed.x)
    setYDraft(seed.y)
    setDescriptionDraft(seed.description)
    setErrors(NO_ERRORS)
  }

  // Stable and unique per instance, with no counter to get out of step when
  // React double-renders under StrictMode.
  const baseId = useId()
  const headingId = `${baseId}-heading`
  const yearId = `${baseId}-year`
  const xId = `${baseId}-x`
  const yId = `${baseId}-y`
  const descriptionId = `${baseId}-description`
  const descriptionCountId = `${baseId}-description-count`

  useEffect(() => {
    // `key` is the only dependency on purpose: read `target` from the render
    // that changed it. App hands over a fresh object every render, so depending
    // on the object itself would reset the drafts while the user types.
    if (target === null) return

    const dialog = dialogRef.current
    if (dialog === null) return

    // The drafts were reset in the render that opened this target; a fresh
    // opening starts from no error too, because the previous attempt was either
    // saved, deleted or cancelled.
    cancelHandledRef.current = false
    pointerDownOutsideRef.current = false

    const active = document.activeElement
    returnFocusRef.current =
      active instanceof HTMLElement && active !== document.body ? active : null

    // `open` is checked so a StrictMode double effect cannot throw
    // `InvalidStateError` on an already-open dialog.
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal()
    } else {
      dialog.open = true
    }

    yearRef.current?.focus()
    yearRef.current?.select()

    return () => {
      cancelHandledRef.current = false
      pointerDownOutsideRef.current = false
      if (typeof dialog.close === 'function' && dialog.open) dialog.close()
      else dialog.open = false
      // The platform restores focus on `close()`, but the element is removed by
      // React before that, so restore it here. When the opener is gone (the
      // entry was deleted) this is a no-op and the caller moves focus on.
      // `preventScroll` because the opener can be a legend row far below the
      // fold, and focusing it must not drag the viewport back down to it.
      returnFocusRef.current?.focus({ preventScroll: true })
    }
  }, [key])

  /** Cancel through `onCancel`, at most once per opening. */
  function requestCancel(): void {
    if (cancelHandledRef.current) return
    cancelHandledRef.current = true
    onCancel()
  }

  function handleYearChange(text: string): void {
    setYearDraft(text)
    // Not live validation: a message only exists because a save was attempted,
    // and it clears the moment the text parses. Text that is still invalid
    // keeps its message, so a flag never lies about the current value.
    setErrors((current) =>
      current.year !== null && isValidYear(parseNumber(text)) ? { ...current, year: null } : current,
    )
  }

  function handleXChange(text: string): void {
    setXDraft(text)
    setErrors((current) =>
      current.x !== null && isValidCoord(parseNumber(text)) ? { ...current, x: null } : current,
    )
  }

  function handleYChange(text: string): void {
    setYDraft(text)
    setErrors((current) =>
      current.y !== null && isValidCoord(parseNumber(text)) ? { ...current, y: null } : current,
    )
  }

  /** Validate every field and save, or mark each offender. One path, many triggers. */
  function submitDrafts(): void {
    const year = parseNumber(yearDraft)
    const x = parseNumber(xDraft)
    const y = parseNumber(yDraft)

    const problems: FieldErrors = {
      year: isValidYear(year) ? null : YEAR_ERROR,
      x: isValidCoord(x) ? null : COORD_ERROR,
      y: isValidCoord(y) ? null : COORD_ERROR,
    }

    if (problems.year !== null || problems.x !== null || problems.y !== null) {
      setErrors(problems)
      // Send the caret to the first field that needs attention, the way the
      // year prompt focused its only field.
      if (problems.year !== null) yearRef.current?.focus()
      else if (problems.x !== null) xRef.current?.focus()
      else yRef.current?.focus()
      return
    }

    onSave({
      year,
      // The single rounding the store, the share link and the display use, so
      // this dialog cannot introduce a precision only it has.
      x: roundCoord(x),
      y: roundCoord(y),
      description: descriptionDraft.trim(),
    })
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    // Not a `method="dialog"` form: an invalid field must keep the dialog open,
    // and a valid one is saved by the caller.
    event.preventDefault()
    submitDrafts()
  }

  function handleFieldKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key !== 'Enter') return
    // Enter is a browser's implicit form submission, which jsdom does not
    // implement at all, so it is requested explicitly. `preventDefault` stops a
    // real browser from submitting a second time, and `requestSubmit` runs the
    // same submit event the button does, so the triggers cannot drift.
    event.preventDefault()
    const form = event.currentTarget.form
    if (form !== null && typeof form.requestSubmit === 'function') form.requestSubmit()
    else submitDrafts()
  }

  function handleDialogCancel(event: SyntheticEvent<HTMLDialogElement>): void {
    // Let the caller's state decide: the platform's own close would leave React
    // still rendering an open dialog.
    event.preventDefault()
    requestCancel()
  }

  /**
   * Remember where the press started.
   *
   * A text selection that begins in a field and is released over the backdrop
   * produces a click whose target is the dialog element and whose coordinates
   * are outside it — the very shape a backdrop click has — so the click alone
   * cannot be trusted. This is the condition that separates the two.
   */
  function handleDialogPointerDown(event: PointerEvent<HTMLDialogElement>): void {
    const rect = dialogRef.current?.getBoundingClientRect()
    pointerDownOutsideRef.current =
      rect !== undefined && hasBox(rect) && isOutside(rect, event.clientX, event.clientY)
  }

  /**
   * Dismiss on a click on the backdrop, through the same guarded `requestCancel`
   * path as Escape, the platform's `cancel` event and the Cancel button, so one
   * dismissal can never call `onCancel` twice and nothing else is called.
   *
   * `::backdrop` has no DOM node, so a backdrop click targets the dialog element
   * — as a click on the dialog's own padding does, and as a text selection
   * released over the backdrop does. The three are told apart by requiring the
   * target to be the dialog, the coordinates to lie outside its measured box,
   * and the preceding press to have started outside that box too. An
   * unmeasurable box dismisses nothing.
   */
  function handleDialogClick(event: MouseEvent<HTMLDialogElement>): void {
    const dialog = dialogRef.current
    if (dialog === null || event.target !== dialog) return

    const rect = dialog.getBoundingClientRect()
    if (!hasBox(rect)) return
    if (!isOutside(rect, event.clientX, event.clientY)) return
    if (!pointerDownOutsideRef.current) return

    requestCancel()
  }

  function handleDialogKeyDown(event: KeyboardEvent<HTMLDialogElement>): void {
    if (event.key !== 'Escape') return
    // A browser closes a modal dialog on Escape itself (the `cancel` event
    // above); jsdom implements neither, so the key is handled here too and the
    // guard keeps the two paths to a single cancel.
    event.preventDefault()
    requestCancel()
  }

  if (target === null) return null

  const isEdit = target.mode === 'edit'
  const heading = isEdit ? `Edit point for ${target.entry.year}` : 'Add a point'

  return (
    <dialog
      ref={dialogRef}
      className="card point-dialog"
      data-testid="point-dialog"
      aria-labelledby={headingId}
      onCancel={handleDialogCancel}
      onKeyDown={handleDialogKeyDown}
      onPointerDown={handleDialogPointerDown}
      onClick={handleDialogClick}
    >
      <form onSubmit={handleSubmit} noValidate className="point-dialog__form">
        {/* The card's own title style, with its bottom margin opted out: the
            form's gap already sets the rhythm between the dialog's blocks. */}
        <h2 id={headingId} className="card__title point-dialog__heading">
          {heading}
        </h2>

        <NumericField
          id={yearId}
          testId="point-dialog-field-year"
          label="Year"
          inputMode="numeric"
          value={yearDraft}
          error={errors.year}
          inputRef={yearRef}
          onChange={handleYearChange}
          onKeyDown={handleFieldKeyDown}
        />

        {/* The two coordinates share one row, defined by the two-column grid on
            this wrapper in app.css (with `minmax(0, 1fr)` so an input's own
            intrinsic width cannot stop the pair shrinking). Each still carries
            its own label, error line and `aria-describedby`; only the axis of
            the layout changed. */}
        <div className="point-dialog__coords" data-testid="point-dialog-coords">
          <NumericField
            id={xId}
            testId="point-dialog-field-x"
            label="Economic x"
            inputMode="decimal"
            value={xDraft}
            error={errors.x}
            inputRef={xRef}
            onChange={handleXChange}
            onKeyDown={handleFieldKeyDown}
          />

          <NumericField
            id={yId}
            testId="point-dialog-field-y"
            label="Social y"
            inputMode="decimal"
            value={yDraft}
            error={errors.y}
            inputRef={yRef}
            onChange={handleYChange}
            onKeyDown={handleFieldKeyDown}
          />
        </div>

        <div className="point-dialog__field">
          <label htmlFor={descriptionId}>Description</label>
          <input
            id={descriptionId}
            className="field__input"
            data-testid="point-dialog-field-description"
            type="text"
            autoComplete="off"
            maxLength={MAX_DESCRIPTION_LENGTH}
            value={descriptionDraft}
            aria-describedby={descriptionCountId}
            onChange={(event) => setDescriptionDraft(event.target.value)}
            onKeyDown={handleFieldKeyDown}
          />
          {/* Passive on purpose: announcing every keystroke would drown out the
              error alerts above. It stays reachable as the input's
              `aria-describedby` target. */}
          <span className="field__count" id={descriptionCountId} aria-live="off">
            {descriptionDraft.length}/{MAX_DESCRIPTION_LENGTH}
          </span>
        </div>

        <div className="point-dialog__actions">
          <button
            type="button"
            className="button button--secondary point-dialog__button"
            onClick={requestCancel}
          >
            Cancel
          </button>
          {/* Enter in any field requests the form's submit event, which is the
              same path as pressing this button. */}
          <button
            type="submit"
            className="button button--primary point-dialog__button"
            data-testid="point-dialog-save"
          >
            {isEdit ? 'Save changes' : 'Add this point'}
          </button>
          {/* Destructive and unconfirmed, matching the app's convention that
              only Clear all asks — this is the only delete for a point. It sits
              last so it is never the first tab stop, the way the toolbar ends
              with Clear all. */}
          {isEdit ? (
            <button
              type="button"
              className="button button--danger point-dialog__button"
              data-testid="point-dialog-delete"
              onClick={onDelete}
            >
              Delete
            </button>
          ) : null}
        </div>
      </form>
    </dialog>
  )
}
